// Tool initialization, tool cycling, tool context setup
import type { EditorState } from "./state/EditorState";
import type { Document } from "../core/document/Document";
import type { Tool, ToolContext, PointerSample } from "../core/tools/Tool";
import type { Viewport } from "../core/render/Viewport";
import type { RGB } from "../core/tools/Tool";
import type { Command } from "../core/commands/Command";

export interface ToolContextDeps {
  state: EditorState;
  showTip: (msg: string) => void;
}

export function toolCtx(deps: ToolContextDeps): ToolContext {
  return {
    doc: deps.state.doc,
    viewport: deps.state.viewport,
    fgColor: deps.state.fgColor,
    bgColor: deps.state.bgColor,
    quickMask: deps.state.quickMask,
    autoSelect: deps.state.autoSelect,
    autoSelectMode: deps.state.autoSelectMode,
    selectedLayerIds: deps.state.selectedLayerIds,
    emit: (cmd: Command) => deps.state.execute(cmd),
    emitTransient: (cmd: Command) => deps.state.applyTransient(cmd),
    setViewport: (patch: Partial<Viewport>) => deps.state.setViewport(patch),
    setForegroundColor: (c: RGB) => deps.state.setForegroundColor(c),
    setBackgroundColor: (c: RGB) => deps.state.setBackgroundColor(c),
    requestRender: () => deps.state.requestRender(),
    showTip: (msg: string) => deps.showTip(msg),
    selectLayer: (id: string) => deps.state.setActiveLayer(id),
  };
}

export function sampleFromEvent(e: PointerEvent): PointerSample {
  const canvas = document.getElementById("stage") as HTMLCanvasElement;
  const r = canvas.getBoundingClientRect();
  return {
    x: e.clientX - r.left,
    y: e.clientY - r.top,
    pressure: e.pressure,
    tiltX: e.tiltX,
    tiltY: e.tiltY,
    timeStamp: e.timeStamp,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
    ctrlKey: e.ctrlKey,
    metaKey: e.metaKey,
  };
}

export function highlightToolByShortcut(
  key: string,
  railButtons: Map<string, HTMLButtonElement>,
  // i152/i157: when the caller knows the SPECIFIC flyout variant that just
  // became active (e.g. "Elliptical Marquee" for the M slot), pass its label
  // here so the rail button's own title/aria-label — not just the
  // options-bar badge below — reflects it. Icon href is left untouched:
  // index.html ships exactly one icon symbol per tool-rail group, so there
  // is no distinct per-variant icon asset to swap to.
  variantLabel?: string,
): void {
  for (const btn of railButtons.values()) btn.classList.remove("is-active");
  const btn = railButtons.get(key);
  if (!btn) return;
  btn.classList.add("is-active");
  if (variantLabel) {
    const fullLabel = `${variantLabel} (${key})`;
    btn.title = fullLabel;
    btn.setAttribute("aria-label", fullLabel);
  }
  const title = btn.getAttribute("title") ?? "";
  const name = variantLabel ?? title.split(" (")[0] ?? "";
  const label = document.querySelector<HTMLSpanElement>(".optionsbar__tool span");
  if (label) label.textContent = name;
  const iconUse = btn.querySelector<SVGUseElement>(".ico use");
  const targetUse = document.querySelector<SVGUseElement>(".optionsbar__tool .ico use");
  if (iconUse && targetUse) {
    const href = iconUse.getAttribute("href");
    if (href) targetUse.setAttribute("href", href);
  }
}

export function initRailButtons(): Map<string, HTMLButtonElement> {
  const railButtons = new Map<string, HTMLButtonElement>();
  document.querySelectorAll<HTMLButtonElement>(".tool").forEach((btn) => {
    const key = btn.getAttribute("data-shortcut");
    if (key) railButtons.set(key, btn);
  });
  return railButtons;
}

export function wireToolRailClicks(
  tools: Record<string, Tool>,
  state: EditorState,
  railButtons: Map<string, HTMLButtonElement>,
): void {
  document.querySelectorAll<HTMLButtonElement>(".tool").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.getAttribute("data-shortcut") ?? "";
      if (tools[key]) state.setActiveTool(tools[key]);
      // highlight handled by caller
    });
  });
}
